import { useState, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useForm, useWatch } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/contexts/AuthContext'
import type { Account } from '@limo/database'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Switch } from '@/components/ui/switch'
import { Label } from '@/components/ui/label'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { useToast } from '@/hooks/use-toast'
import { Loader2 } from 'lucide-react'

// Schema for invitation mode (no password required)
const baseSchema = z.object({
  email: z.string().email('Invalid email address'),
  full_name: z.string().min(1, 'Full name is required'),
  role: z.enum(['admin', 'user']),
  account_id: z.string().nullable(),
})

// Schema with password validation for manual creation mode
const createUserSchema = baseSchema.extend({
  password: z.string().min(8, 'Password must be at least 8 characters'),
  confirm_password: z.string(),
}).refine((data) => data.password === data.confirm_password, {
  message: 'Passwords do not match',
  path: ['confirm_password'],
}).refine((data) => {
  // Account is required for user role
  if (data.role === 'user' && !data.account_id) {
    return false
  }
  return true
}, {
  message: 'Account is required for users',
  path: ['account_id'],
})

// Schema for invitation mode
const inviteUserSchema = baseSchema.refine((data) => {
  // Account is required for user role
  if (data.role === 'user' && !data.account_id) {
    return false
  }
  return true
}, {
  message: 'Account is required for users',
  path: ['account_id'],
})

type CreateUserFormData = z.infer<typeof createUserSchema>
type InviteUserFormData = z.infer<typeof inviteUserSchema>

interface CreateUserDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function CreateUserDialog({ open, onOpenChange }: CreateUserDialogProps) {
  const [sendInvitation, setSendInvitation] = useState(true)
  const queryClient = useQueryClient()
  const { toast } = useToast()
  const { user: currentUser } = useAuth()

  const { data: accounts } = useQuery({
    queryKey: ['accounts-select'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('accounts')
        .select('id, name')
        .eq('status', 'active')
        .order('name')
      if (error) throw error
      return data as Pick<Account, 'id' | 'name'>[]
    },
  })

  const form = useForm<CreateUserFormData>({
    resolver: zodResolver(sendInvitation ? inviteUserSchema : createUserSchema),
    defaultValues: {
      email: '',
      full_name: '',
      role: 'user',
      account_id: null,
      password: '',
      confirm_password: '',
    },
  })

  // Watch the role field to conditionally show/hide Account field
  const watchedRole = useWatch({ control: form.control, name: 'role' })

  // Auto-clear account_id when role changes to admin
  useEffect(() => {
    if (watchedRole === 'admin') {
      form.setValue('account_id', null)
    }
  }, [watchedRole, form])

  // Clear fields and errors when sendInvitation changes
  useEffect(() => {
    // Clear password fields when switching to invitation mode
    if (sendInvitation) {
      form.setValue('password', '')
      form.setValue('confirm_password', '')
    }
    // Clear validation errors - validation will happen on submit
    form.clearErrors()
  }, [sendInvitation, form])

  // Mutation for invitation mode
  const inviteMutation = useMutation({
    mutationFn: async (data: InviteUserFormData) => {
      const { data: tokenData, error: tokenError } = await supabase.rpc(
        'generate_invitation_token'
      )
      if (tokenError) throw tokenError

      const { data: invitation, error } = await supabase
        .from('invitations')
        .insert({
          email: data.email.trim().toLowerCase(),
          role: data.role,
          account_id: data.account_id,
          invited_by: currentUser?.id,
          token: tokenData,
          expires_at: new Date(
            Date.now() + 7 * 24 * 60 * 60 * 1000
          ).toISOString(),
        })
        .select('id')
        .single()
      if (error) throw error

      // Send the invitation email (recipient/token are read from the DB row)
      const { error: emailError } = await supabase.functions.invoke(
        'send-notification-email',
        { body: { type: 'invite', invitation_id: invitation.id } }
      )

      return { token: tokenData, emailSent: !emailError }
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['invitations'] })
      queryClient.invalidateQueries({ queryKey: ['users'] })
      if (data.emailSent) {
        toast({
          title: 'Invitation sent',
          description: 'An invitation email has been sent to the user.',
        })
      } else {
        toast({
          title: 'Invitation created',
          description: `Email could not be sent — share this link instead: ${window.location.origin}/accept-invite/${data.token}`,
          variant: 'destructive',
        })
      }
      form.reset()
      onOpenChange(false)
    },
    onError: (error) => {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      })
    },
  })

  // Mutation for manual user creation via Edge Function
  const createUserMutation = useMutation({
    mutationFn: async (data: CreateUserFormData) => {
      const { data: response, error } = await supabase.functions.invoke('create-user', {
        body: {
          email: data.email,
          password: data.password,
          full_name: data.full_name,
          role: data.role,
          account_id: data.account_id,
        },
      })

      if (error) throw error
      if (response?.error) throw new Error(response.error)

      return response
    },
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: ['users'] })
      toast({
        title: 'User created',
        description: response?.email_sent
          ? 'The user has been created and a welcome email was sent.'
          : 'The user has been created, but the welcome email could not be sent. Share the credentials with them directly.',
      })
      form.reset()
      onOpenChange(false)
    },
    onError: (error) => {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      })
    },
  })

  const onSubmit = (data: CreateUserFormData) => {
    if (sendInvitation) {
      inviteMutation.mutate(data)
    } else {
      createUserMutation.mutate(data)
    }
  }

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      form.reset()
      setSendInvitation(true)
    }
    onOpenChange(open)
  }

  const isPending = inviteMutation.isPending || createUserMutation.isPending

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Create New User</DialogTitle>
          <DialogDescription>
            Add a new user to the platform. Choose to send an invitation or set the password directly.
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Email</FormLabel>
                  <FormControl>
                    <Input placeholder="user@example.com" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="full_name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Full Name</FormLabel>
                  <FormControl>
                    <Input placeholder="John Doe" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="role"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Role</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select role" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="admin">Admin</SelectItem>
                      <SelectItem value="user">User</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormDescription>
                    Admins have full platform access. Users are scoped to their account.
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            {watchedRole === 'user' && (
              <FormField
                control={form.control}
                name="account_id"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Account</FormLabel>
                    <Select
                      onValueChange={(value) => field.onChange(value === 'none' ? null : value)}
                      value={field.value || 'none'}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select account" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="none">Select an account...</SelectItem>
                        {accounts?.map((account) => (
                          <SelectItem key={account.id} value={account.id}>
                            {account.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormDescription>
                      The account this user will be assigned to.
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}

            <div className="space-y-3 rounded-lg border p-4">
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label htmlFor="send-invitation">Send invitation to set password</Label>
                  <p className="text-sm text-muted-foreground">
                    {sendInvitation
                      ? 'An email will be sent to the user with a link to set their password.'
                      : 'You will set the password for this user directly.'}
                  </p>
                </div>
                <Switch
                  id="send-invitation"
                  checked={sendInvitation}
                  onCheckedChange={setSendInvitation}
                />
              </div>
            </div>

            {!sendInvitation && (
              <>
                <FormField
                  control={form.control}
                  name="password"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Password</FormLabel>
                      <FormControl>
                        <Input type="password" placeholder="Enter password" {...field} />
                      </FormControl>
                      <FormDescription>
                        Minimum 8 characters.
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="confirm_password"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Confirm Password</FormLabel>
                      <FormControl>
                        <Input type="password" placeholder="Confirm password" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </>
            )}

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => handleOpenChange(false)}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={isPending}>
                {isPending && (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                )}
                Create User
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  )
}
